import React, { useEffect, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Alert, Box, Button, CircularProgress, Collapse, Link, Stack, Typography } from '@mui/material';

import { useGetChatTrace } from '~/hooks/apis/trip-planner/useGetChatTrace';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import { formatDateSlashesWithClockInSydney } from '~/services/TimeService';

import ChatTraceEntry from './components/ChatTraceEntry';
import ChatTraceError from './components/ChatTraceError';
import ChatTraceOtherItems from './components/ChatTraceOtherItems';

export default function TripPlannerChatTracePage({
  renderHeader,
}: {
  renderHeader: (actions?: React.ReactNode) => React.ReactNode;
}) {
  const { chatId } = useParams<{ chatId: string }>();
  const traceQuery = useGetChatTrace(chatId);
  const [turnExpansion, setTurnExpansion] = useState<{
    chatId: string;
    defaultExpanded: boolean;
    overrides: Record<string, boolean>;
  }>({ chatId, defaultExpanded: true, overrides: {} });
  const expansion = turnExpansion.chatId === chatId ? turnExpansion : { chatId, defaultExpanded: true, overrides: {} };
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetching, isFetchNextPageError, fetchNextPage } = traceQuery;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [chatId]);
  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasNextPage || isFetching || isFetchNextPageError) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          void fetchNextPage();
        }
      },
      { rootMargin: '300px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasNextPage, isFetching, isFetchNextPageError, fetchNextPage]);
  const { tenant: currentTenant } = useCurrentTenant();
  const createdBy = traceQuery.data?.result.chat.createdBy;
  const userSummaryQuery = useGetUsersSummaryByIds({
    userIds: createdBy ? [createdBy] : [],
    brand: currentTenant.brand,
    enabled: Boolean(createdBy),
  });

  if (traceQuery.isLoading) {
    return (
      <>
        {renderHeader()}
        <CircularProgress />
      </>
    );
  }
  if (traceQuery.data?.status !== 200) {
    return (
      <>
        {renderHeader()}
        <ChatTraceError error={traceQuery.error} />
      </>
    );
  }

  const trace = traceQuery.data.result;
  const turns = trace.turns.filter((turn) => turn.entries.length > 0);
  const expandedTurnCount = turns.filter(
    (turn) => expansion.overrides[turn.entries[0].id] ?? expansion.defaultExpanded,
  ).length;
  const messageCounts = turns.reduce(
    (counts, turn) => {
      for (const entry of turn.entries) {
        counts[entry.role] += 1;
      }
      return counts;
    },
    { user: 0, assistant: 0 },
  );
  const customer = userSummaryQuery.data?.get(trace.chat.createdBy);
  return (
    <Box sx={{ bgcolor: 'background.paper' }}>
      <Box sx={{ mr: { md: '260px' }, pr: { xs: 1.5, md: 3 } }}>
        {renderHeader(
          turns.length > 0 && (
            <Stack component="header" direction="row" justifyContent="flex-end" flexWrap="wrap" gap={1}>
              <Button
                size="small"
                color="inherit"
                disabled={expandedTurnCount === 0}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
                onClick={() => setTurnExpansion({ chatId, defaultExpanded: false, overrides: {} })}
              >
                Collapse all turns
              </Button>
              <Button
                size="small"
                color="inherit"
                disabled={expandedTurnCount === turns.length}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
                onClick={() => setTurnExpansion({ chatId, defaultExpanded: true, overrides: {} })}
              >
                Expand all turns
              </Button>
            </Stack>
          ),
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack spacing={3} sx={{ p: { xs: 1.5, md: 3 } }}>
            {turns.map((turn, index) => {
              const assistantCount = turn.entries.filter((entry) => entry.role === 'assistant').length;
              const turnId = turn.entries[0].id;
              const expanded = expansion.overrides[turnId] ?? expansion.defaultExpanded;
              const contentId = `trace-${chatId}-turn-${turnId}`;
              return (
                <Stack
                  component="section"
                  aria-label={`Turn ${index + 1}`}
                  key={turnId}
                  sx={{
                    p: { xs: 1.5, md: 2.5 },
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 3,
                    bgcolor: '#f7f9f8',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" gap={1}>
                    <Button
                      size="small"
                      startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      aria-label={`${expanded ? 'Collapse' : 'Expand'} turn ${index + 1}`}
                      aria-expanded={expanded}
                      aria-controls={contentId}
                      onClick={() =>
                        setTurnExpansion({
                          ...expansion,
                          overrides: { ...expansion.overrides, [turnId]: !expanded },
                        })
                      }
                      sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 600 }}
                    >
                      Turn {index + 1}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {assistantCount.toLocaleString()} assistant {assistantCount === 1 ? 'message' : 'messages'}
                    </Typography>
                  </Stack>
                  <Collapse in={expanded} id={contentId}>
                    <Stack spacing={2} sx={{ pt: 2 }}>
                      {turn.entries.map((entry) => (
                        <ChatTraceEntry
                          key={`${entry.role}-${'itemSeq' in entry ? entry.itemSeq : entry.messageSeq}-${entry.id}`}
                          entry={entry}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </Stack>
              );
            })}
          </Stack>
          <ChatTraceOtherItems items={trace.unassignedItems} />
          {hasNextPage && (
            <Box ref={loadMoreRef} sx={{ px: 3, py: 2 }}>
              {traceQuery.isFetchingNextPage && <CircularProgress size={20} aria-label="Loading more messages" />}
              {isFetchNextPageError && (
                <Alert severity="error" action={<Button onClick={() => void fetchNextPage()}>Retry</Button>}>
                  Could not load more messages. Your loaded messages are still here.
                </Alert>
              )}
            </Box>
          )}
          {trace.turns.every((turn) => turn.entries.length === 0) && trace.unassignedItems.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 4 }}>
              No messages have been recorded for this chat.
            </Typography>
          )}
        </Box>
        <Box
          component="aside"
          sx={(theme) => ({
            width: { xs: 'auto', md: 260 },
            flexShrink: 0,
            borderLeft: { md: `1px solid ${theme.palette.grey[300]}` },
            borderBottom: { xs: `1px solid ${theme.palette.grey[300]}`, md: 0 },
          })}
        >
          <Stack spacing={1} sx={{ p: 2, position: 'sticky', top: 0 }}>
            <Typography variant="subtitle2">Properties</Typography>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Typography variant="caption" color="text.secondary">
                Chat ID
              </Typography>
              <Typography variant="caption">{trace.chat.id}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Typography variant="caption" color="text.secondary">
                Name
              </Typography>
              {customer ? (
                <Link href={`/users/${trace.chat.createdBy}`} variant="caption" target="_blank" rel="noreferrer">
                  {customer.full_name}
                </Link>
              ) : (
                <Typography variant="caption">{userSummaryQuery.isLoading ? '…' : 'Customer unavailable'}</Typography>
              )}
            </Stack>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Typography variant="caption" color="text.secondary">
                Created (Sydney)
              </Typography>
              <Typography variant="caption">{formatDateSlashesWithClockInSydney(trace.chat.createdAt)}</Typography>
            </Stack>
            {[
              ['User messages', messageCounts.user],
              ['Assistant messages', messageCounts.assistant],
              ['Total messages', messageCounts.user + messageCounts.assistant],
            ].map(([label, count]) => (
              <Stack key={label} direction="row" justifyContent="space-between" spacing={2}>
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="caption">{count.toLocaleString()}</Typography>
              </Stack>
            ))}
            {hasNextPage && (
              <Typography variant="caption" color="text.secondary">
                Counts cover loaded turns only.
              </Typography>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
